import axios from 'axios'
import userManager from 'Oidc/userManager'
import Const from 'Utils/Consts'
import showResponseError from 'Utils/showResponseError'
import { refreshToken } from './httpConfig'
import type { AxiosRequestConfig, AxiosError, AxiosInstance } from 'axios'

export const baseUrl: string = Const.env.REACT_APP_API_GATEWAY_URL
export const baseIdentityUrl: string = Const.env.REACT_APP_IDENTITY_URL
export const basePodUrl: string = Const.env.REACT_APP_POD_SPACE_URL

// ------------------------------
//  Create base axios instance
// ------------------------------

interface CreateAxiosOptions {
  baseURL: string
  useAuth?: boolean
  timeout?: number
}

export const createAxiosInstance = ({
  baseURL,
  useAuth = true,
  timeout = 10000,
}: CreateAxiosOptions): AxiosInstance => {
  const instance: AxiosInstance = axios.create({
    baseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  })

  // Add a request interceptor to set the Authorization header if a token is available
  if (useAuth) {
    instance.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const user = await userManager.getUser()
        const token = user?.access_token

        if (token)
          config.headers!.Authorization = `${Const.env.REACT_APP_REACT_FAKE_USER == false ? 'Bearer' : 'Fake'} ${token}`
          // config.headers!.Authorization = `Bearer ${token}`
        return config
      },
      error => Promise.reject(error),
    )
  }

  // Add a response interceptor to handle refresh token and errors
  instance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }
      const skipToast = (error.config as any)?.skipToast

      if (!skipToast) {
        showResponseError(error)
      }

      if (error.response?.status === 401 && !originalRequest._retry && useAuth) {
        // originalRequest._retry = true
        try {
          const user = await userManager.getUser()
          if (user?.refresh_token) {
            const newUser = await refreshToken(user)
            if (newUser?.access_token) {
              // retry with new token
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `${Const.env.REACT_APP_REACT_FAKE_USER == false ? 'Bearer' : 'Fake'} ${newUser.access_token}`,
                // Authorization: `Bearer ${newUser.access_token}`,
              }
              return instance(originalRequest)
            }
          }
          // no refresh token or refresh failed → logout
          // await userManager.signoutRedirect()
        } catch (refreshError) {
          console.error('🔴 Token refresh failed:', refreshError)
          // await userManager.signoutRedirect()
        }
      }

      return Promise.reject(error)
    },
  )

  return instance
}

export const instance = createAxiosInstance({ baseURL: baseUrl })
export const identityInstance = createAxiosInstance({ baseURL: baseIdentityUrl, useAuth: false })
export const podInstance = createAxiosInstance({ baseURL: basePodUrl, useAuth: false })
